/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'

// Chakra Components
import {Box, Flex, Stack, HStack, Text, useStyleConfig, useMediaQuery, Fade} from '@chakra-ui/react'
import Link from '@salesforce/retail-react-app/app/components/link'
import {breakpoints} from '../../../../theme/_utils'

// Project Components
//import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import ItemVariantProvider from '@salesforce/retail-react-app/app/components/item-variant'
import CartItemVariantImage from '../../../../components/item-variant/item-image'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import CartItemVariantName from '../../../../components/item-variant/item-name'
import {useCurrency, useVariant} from '@salesforce/retail-react-app/app/hooks'
import DisplayPrice from '../../../../components/display-price'
import OmnibusPrice from '../../../../components/display-price/omnibus-price'
import StockLabel from '../../../../components/stock-label'
import {InfoIcon} from '../../../../components/icons/others'
import CloseIcon from '../../../../components/icons/action/CloseIcon'
import AddToCartButton from '../../../../components/add-to-cart-button'
import NotifyMeButton from '../../../../components/notify-me-button'
import {ADD_TO_CART_ERROR_KEYS, ADD_TO_CART_ERROR_MESSAGES} from '../../../../constants'
import {InformativeLabelGroup} from '../../../../components/informative-label'
import useProductData from '../../../../hooks/use-product-data'
import {PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID} from '@salesforce/retail-react-app/app/constants'
import {useBooleanCustomPreference} from '../../../../hooks/use-custom-preference'

/**
 * Component representing a product item usually in a list with details about the product - name, variant, pricing, etc.
 * @param {Object} product Product to be represented in the list item.
 * @param {node} primaryAction Child component representing the most prominent action to be performed by the user.
 * @param {node} secondaryActions Child component representing the other actions relevant to the product to be performed by the user.
 * @param {func} onItemQuantityChange callback function to be invoked whenever item quantity changes.
 * @param {boolean} showLoading Renders a loading spinner with overlay if set to true.
 * @returns A JSX element representing product item in a list (eg: wishlist, cart, etc).
 */
const WishlistProductItem = ({product, secondaryActions, showLoading = false}) => {
    const {currency} = useCurrency()
    const {formatMessage} = useIntl()
    const style = useStyleConfig('Wishlist')
    const [isMobile] = useMediaQuery(`(max-width: ${breakpoints.md})`)
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')
    const [showErrorMessage, setShowErrorMessage] = useState(0)
    const errorContainerRef = useRef(null)
    const variant = useVariant(product)

    product = {...product, ...variant}
    const {productUrl, priceData} = useProductData(product, PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID)

    const lastItemThreshold = useBooleanCustomPreference('showLastItemThreshold')

    return (
        <Box
            {...style?.itemContainer}
            data-testid={`sf-cart-item-${product.productId ? product.productId : product.id}`}
        >
            <ItemVariantProvider variant={product}>
                {showLoading && <LoadingSpinner />}
                <Stack {...style?.flexContainer}>
                    <Stack {...style?.productDetails}>
                        <Link to={productUrl} {...style?.productImageContainer}>
                            <CartItemVariantImage
                                {...style?.productImage}
                                imageProps={style?.cartImage}
                            />
                            <Flex {...style?.removeIcon}>{secondaryActions}</Flex>
                            <HStack {...style.badgeGroup}>
                                <InformativeLabelGroup product={product} priceData={priceData} />
                            </HStack>
                        </Link>
                        {product?.inventory?.stockLevel <= lastItemThreshold &&
                            product?.inventory?.stockLevel !== 0 && (
                                <StockLabel
                                    variant="lastFewItems"
                                    icon={<InfoIcon circleColor="transparent" />}
                                    textMessage={
                                        <FormattedMessage
                                            id="use_product.message.low_stock"
                                            defaultMessage="Last {quantity} {itemLabel}"
                                            values={{
                                                quantity: product?.inventory?.stockLevel,
                                                itemLabel:
                                                    product?.inventory?.stockLevel === 1 ? (
                                                        <FormattedMessage
                                                            id="global.message.item"
                                                            defaultMessage="item"
                                                        />
                                                    ) : (
                                                        <FormattedMessage
                                                            id="global.message.items"
                                                            defaultMessage="items"
                                                        />
                                                    ),
                                            }}
                                        />
                                    }
                                />
                            )}
                        {product?.inventory?.stockLevel === 0 && (
                            <StockLabel
                                variant="outOfStock"
                                icon={<CloseIcon />}
                                textMessage={
                                    <FormattedMessage
                                        id="use_product.message.out_of_stock"
                                        defaultMessage="Out of stock"
                                    />
                                }
                            />
                        )}
                        <Box {...style?.detailContainer}>
                            <Box {...style?.headerStack}>
                                <Link to={productUrl}>
                                    <CartItemVariantName {...style?.itemName} />
                                </Link>
                            </Box>
                            <Flex flexDirection={'column'}>
                                <DisplayPrice
                                    priceData={priceData}
                                    currency={currency}
                                    listPriceProps={{...style.productListPrice}}
                                    currentPriceProps={{...style.productDetailPrice}}
                                    discountProps={{display: 'none'}}
                                    productDetailPriceView
                                    notShoppableCountry={notShoppableCountry}
                                />
                                {!notShoppableCountry && priceData.showOmnibusPriceDiscount && (
                                    <Box {...style.productOmnibusWrapper}>
                                        <OmnibusPrice
                                            priceData={priceData}
                                            currency={currency}
                                            omnibusExpandedView
                                            variant="recommended"
                                        />
                                    </Box>
                                )}
                            </Flex>
                            <Box ref={errorContainerRef}>
                                {showErrorMessage ? (
                                    <Fade in={true}>
                                        <Text color="text.danger" fontWeight={600} marginBottom={8}>
                                            {showErrorMessage ===
                                            ADD_TO_CART_ERROR_KEYS.INVENTORY_REMAINING_FOR_PRODUCT
                                                ? formatMessage(
                                                      ADD_TO_CART_ERROR_MESSAGES.INVENTORY_REMAINING_FOR_PRODUCT,
                                                      {
                                                          stockLevel:
                                                              product?.c_extendVariant?.stock || 0,
                                                          productName: product.productName,
                                                      },
                                                  )
                                                : formatMessage(
                                                      ADD_TO_CART_ERROR_MESSAGES.NO_VARIANT_SELECTED,
                                                  )}
                                        </Text>
                                    </Fade>
                                ) : (
                                    <></>
                                )}
                            </Box>
                            {!notShoppableCountry && (
                                <Stack w="full">
                                    {product?.inventory?.stockLevel === 0 ||
                                    product?.c_extendVariant?.stock === null ? (
                                        <NotifyMeButton product={product} />
                                    ) : (
                                        <AddToCartButton
                                            product={product}
                                            compactView={isMobile}
                                            setShowErrorMessage={setShowErrorMessage}
                                            errorContainerRef={errorContainerRef}
                                            showUniversalFeedback={true}
                                        />
                                    )}
                                </Stack>
                            )}
                        </Box>
                    </Stack>
                </Stack>
            </ItemVariantProvider>
        </Box>
    )
}

WishlistProductItem.propTypes = {
    product: PropTypes.object,
    onItemQuantityChange: PropTypes.func,
    onAddItemToCart: PropTypes.func,
    showLoading: PropTypes.bool,
    isWishlistItem: PropTypes.bool,
    secondaryActions: PropTypes.node,
}

export default WishlistProductItem
